import { useCallback } from "react";
import { mutate } from "swr";
import wretch from "wretch";
import QueryStringAddon from "wretch/addons/queryString";
import { useAuthContext } from "../context/authContext";
import { FetcherOptions, PaginationQuery, UploadFileResponse } from "../types";

export const mutateCache = (keys: string[], skipKey?: string) => {
  for (const k of keys) {
    mutate(
      (key) => {
        if (typeof key === "string" && key !== skipKey) {
          return key === k || key.toLowerCase().startsWith(`${k}?`);
        }
        return false;
      },
      undefined,
      {
        revalidate: true,
      },
    );
  }
};

export const buildUrl = (url: string, query: PaginationQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value) {
      params.set(key, `${value}`);
    }
  });

  const op = url.includes("?") ? "&" : "?";

  return url + (params.size > 0 ? `${op}${params.toString()}` : "");
};

export const useApi = () => {
  const { expire, jwtToken } = useAuthContext();

  const handleError = useCallback(
    (error: any) => {
      if (error && error.status === 401) {
        // Redirect to the login page
        // window.location.href = "/login"; // Adjust the path as needed
        expire();
      }
      throw error; // Re-throw error for further handling if needed
    },
    [expire],
  );

  const baseClient = wretch().addon(QueryStringAddon).options({ credentials: "include" });
  const authenticatedClient = jwtToken ? baseClient.auth(`Bearer ${jwtToken}`) : baseClient;

  const api = authenticatedClient.url("/api");

  const get = useCallback(
    <Result>({ url, signal }: FetcherOptions) =>
      api.options({ signal }).url(url).get().json<Result>().catch(handleError),
    [api, handleError],
  );

  const post = useCallback(
    <Result, Body>(url: string, body?: Body) => api.url(url).post(body).json<Result>().catch(handleError),
    [api, handleError],
  );

  const postNoResult = useCallback(
    <Body>(url: string, body?: Body) => api.url(url).post(body).res().catch(handleError),
    [api, handleError],
  );

  const put = useCallback(
    <Body>(url: string, body: Body) => api.url(url).put(body).res().catch(handleError),
    [api, handleError],
  );
  const putWithResult = useCallback(
    <Body, Result>(url: string, body: Body) => api.url(url).put(body).json<Result>().catch(handleError),
    [api, handleError],
  );

  const del = useCallback((url: string) => api.url(url).delete().res().catch(handleError), [api, handleError]);
  const delWithResult = useCallback(
    <Result>(url: string) => api.url(url).delete().json<Result>().catch(handleError),
    [api, handleError],
  );

  const uploadFile = useCallback(
    async (signedUrl: string, file: File): Promise<UploadFileResponse> => {
      try {
        const res = await fetch(signedUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
            "Cache-Control": "private, max-age=0, no-transform",
          },
          body: file,
        });
        if (!res.ok) {
          throw new Error(`Upload failed: ${res.status}`);
        }
        return res as UploadFileResponse;
      } catch (error) {
        return handleError(error);
      }
    },
    [handleError],
  );

  const createObjectURL = useCallback(
    (url: string, redirectError?: boolean) =>
      authenticatedClient
        .options({ redirect: redirectError ? "error" : undefined })
        .url(url)
        .get()
        .blob()
        .then((blob) => URL.createObjectURL(blob))
        .catch(handleError),
    [authenticatedClient, handleError],
  );

  const downloadFile = useCallback(
    (url: string, fileName: string) =>
      createObjectURL(url, true)
        .then((res) => {
          const link = document.createElement("a");
          link.href = res;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        })
        .catch(handleError),
    [createObjectURL],
  );

  const downloadExcel = useCallback(
    (url: string, fileName: string) =>
      api
        .headers({ Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        .url(url)
        .get()
        .blob()
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        })
        .catch(handleError),
    [api, handleError],
  );

  return {
    get,
    post,
    postNoResult,
    put,
    putWithResult,
    del,
    delWithResult,
    uploadFile,
    createObjectURL,
    downloadFile,
    downloadExcel,
  };
};
